import { Operations, ProblemFormatOptions } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { operationsOf } from "./FactRanges.ts";

/**
 * The fact fluency ladders (ADR 0071): a short test per level, a level per
 * set of facts, and a fixed order to climb them in.
 *
 * This is the one place that decides what a level holds. The generator
 * (`FactLevelGenerator.ts`), the level picker, the differentiation step and the
 * printed header all ask here, the way every consumer of a value range asks
 * `FactRanges.ts`.
 *
 * **Level numbers are part of the URL contract** (ADR 0071 §1), like seeds.
 * `fl=6` in a link made today must print the same level next year, so a ladder
 * is append-only: a level may be added after the last table and before Mixed
 * only by making a new ladder. `FactLevels.test.ts` snapshots every ladder to
 * hold that.
 *
 * Facts are keyed the way `missedFacts.ts` keys them and `problemFromFactKey`
 * parses them: "7x8", "56÷8", "6+7", "13-6". A key is written the way the
 * problem is printed - left operand on top.
 */

export type FactLadderId =
    | "add"
    | "sub"
    | "addsub"
    | "mul10"
    | "mul12"
    | "div10"
    | "div12"
    | "muldiv10"
    | "muldiv12";

export type FactLevelCeiling = 10 | 12;

/** The languages a level name is written in - the interface locales, which are also the printed ones. */
export type LevelLanguage = "en" | "es" | "fr";

/** What a level is, from which its name is written. */
export type FactLevelName =
    | { kind: "table"; factors: number[] }
    | { kind: "set"; set: AdditionSet }
    | { kind: "mixed" };

export interface FactLevel {
    /** 1-based and stable: never renumbered (see the header). */
    id: number;
    name: FactLevelName;
    /** The facts this level is about. */
    focus: string[];
    /**
     * How much of a test comes from `focus`; the rest is review from earlier
     * levels. 1 for a times table, whose own earlier facts are the review
     * (ADR 0071 §2), and for Mixed; two thirds for an addition set.
     */
    focusShare: number;
}

export interface FactLadder {
    id: FactLadderId;
    levels: FactLevel[];
}

type AdditionSet = "zeroOne" | "two" | "doubles" | "nearDoubles" | "makeTen" | "eight" | "rest";

const range = (from: number, to: number) =>
    Array.from({ length: to - from + 1 }, (_, i) => from + i);

// ─── Multiplication and division ─────────────────────────────────────────

/**
 * The order the site already publishes in the times-tables FAQ ("What order
 * should the times tables be learned in?"): 0 and 1, then 2, 5 and 10, then 3
 * and 4, then 6 and 9, then 7 and 8; 11 and 12 after the rest.
 */
const TABLE_ORDER: number[][] = [[0, 1], [2], [5], [10], [3], [4], [6], [9], [7], [8]];
const TWELVES_EXTRA: number[][] = [[11], [12]];

function tableFacts(factors: number[], ceiling: number): string[] {
    const facts = new Set<string>();
    for (const n of factors) {
        for (const k of range(0, ceiling)) {
            facts.add(`${n}x${k}`);
            facts.add(`${k}x${n}`);
        }
    }
    return [...facts];
}

function allProducts(ceiling: number): string[] {
    const facts: string[] = [];
    for (const a of range(0, ceiling)) {
        for (const b of range(0, ceiling)) {
            facts.push(`${a}x${b}`);
        }
    }
    return facts;
}

function multiplicationLadder(ceiling: FactLevelCeiling): FactLevel[] {
    const tables = ceiling === 12 ? [...TABLE_ORDER, ...TWELVES_EXTRA] : TABLE_ORDER;
    const levels: FactLevel[] = tables.map((factors, index) => ({
        id: index + 1,
        name: { kind: "table", factors },
        focus: tableFacts(factors, ceiling),
        focusShare: 1,
    }));
    levels.push({
        id: levels.length + 1,
        name: { kind: "mixed" },
        focus: allProducts(ceiling),
        focusShare: 1,
    });
    return levels;
}

/** a x b = p becomes p ÷ b = a. A zero divisor has no fact. */
function divisionMirror(facts: string[]): string[] {
    const mirrored = new Set<string>();
    for (const fact of facts) {
        const [a, b] = fact.split("x").map(Number);
        if (b !== 0) {
            mirrored.add(`${a * b}÷${b}`);
        }
    }
    return [...mirrored];
}

// ─── Addition and subtraction ────────────────────────────────────────────

/**
 * Strategy sets over the 100 facts 0+0 to 9+9. A fact belongs to the first set
 * it fits, in this order (ADR 0071 §2), so no fact is in two sets.
 */
const ADDITION_SETS: { set: AdditionSet; fits: (a: number, b: number) => boolean }[] = [
    { set: "zeroOne", fits: (a, b) => a <= 1 || b <= 1 },
    { set: "two", fits: (a, b) => a === 2 || b === 2 },
    { set: "doubles", fits: (a, b) => a === b },
    { set: "nearDoubles", fits: (a, b) => Math.abs(a - b) === 1 },
    { set: "makeTen", fits: (a, b) => a + b === 10 || a === 9 || b === 9 },
    { set: "eight", fits: (a, b) => a === 8 || b === 8 },
    { set: "rest", fits: () => true },
];

/** Review makes up the last third of an addition test from level 2 on. */
const ADDITION_FOCUS_SHARE = 2 / 3;

function additionLadder(): FactLevel[] {
    const buckets: string[][] = ADDITION_SETS.map(() => []);
    for (const a of range(0, 9)) {
        for (const b of range(0, 9)) {
            const index = ADDITION_SETS.findIndex((entry) => entry.fits(a, b));
            buckets[index].push(`${a}+${b}`);
        }
    }
    const levels: FactLevel[] = ADDITION_SETS.map((entry, index) => ({
        id: index + 1,
        name: { kind: "set", set: entry.set },
        focus: buckets[index],
        focusShare: index === 0 ? 1 : ADDITION_FOCUS_SHARE,
    }));
    levels.push({
        id: levels.length + 1,
        name: { kind: "mixed" },
        focus: buckets.flat(),
        focusShare: 1,
    });
    return levels;
}

/** a + b = c becomes c - b = a. */
function subtractionMirror(facts: string[]): string[] {
    return facts.map((fact) => {
        const [a, b] = fact.split("+").map(Number);
        return `${a + b}-${b}`;
    });
}

// ─── Ladders ─────────────────────────────────────────────────────────────

function mirrored(levels: FactLevel[], mirror: (facts: string[]) => string[]): FactLevel[] {
    return levels.map((level) => ({ ...level, focus: mirror(level.focus) }));
}

/** Level k of a fact-family ladder is both operations' level k together. */
function families(first: FactLevel[], second: FactLevel[]): FactLevel[] {
    return first.map((level, index) => ({
        ...level,
        focus: [...level.focus, ...second[index].focus],
    }));
}

function buildLadders(): Record<FactLadderId, FactLevel[]> {
    const add = additionLadder();
    const sub = mirrored(add, subtractionMirror);
    const mul10 = multiplicationLadder(10);
    const mul12 = multiplicationLadder(12);
    const div10 = mirrored(mul10, divisionMirror);
    const div12 = mirrored(mul12, divisionMirror);
    return {
        add,
        sub,
        addsub: families(add, sub),
        mul10,
        mul12,
        div10,
        div12,
        muldiv10: families(mul10, div10),
        muldiv12: families(mul12, div12),
    };
}

const LADDERS = buildLadders();

function ladderIdFor(operations: Operations[], ceiling: FactLevelCeiling): FactLadderId | null {
    const has = (op: Operations) => operations.includes(op);
    if (operations.length === 0 || has(Operations.LongDivision)) {
        return null;
    }
    const additive = has(Operations.Addition) || has(Operations.Subtraction);
    const multiplicative = has(Operations.Multiplication) || has(Operations.Division);
    // "Level 4" of two ladders of different lengths means nothing (ADR 0071 §2).
    if (additive && multiplicative) {
        return null;
    }
    if (additive) {
        if (has(Operations.Addition) && has(Operations.Subtraction)) return "addsub";
        return has(Operations.Addition) ? "add" : "sub";
    }
    const to = ceiling === 12 ? "12" : "10";
    if (has(Operations.Multiplication) && has(Operations.Division)) return `muldiv${to}` as FactLadderId;
    return (has(Operations.Multiplication) ? `mul${to}` : `div${to}`) as FactLadderId;
}

/**
 * The ladder for a set of operations: one operation, or one fact family (+ with
 * -, x with ÷). Anything else - long division, + with x, nothing - has none.
 * The ceiling only matters to multiplication and division.
 */
export function ladderFor(operations: Operations[], ceiling: FactLevelCeiling = 10): FactLadder | null {
    const id = ladderIdFor(operations, ceiling);
    return id ? { id, levels: LADDERS[id] } : null;
}

/** Every ladder by id, for tests and the append-only snapshot. */
export function allLadders(): Record<FactLadderId, FactLevel[]> {
    return LADDERS;
}

export function ceilingOf(settings: Pick<ISettingsProps, "FactLevelCeiling">): FactLevelCeiling {
    return settings.FactLevelCeiling === 12 ? 12 : 10;
}

/** The ladder the sheet's operations select, or null. */
export function ladderForSettings(settings: ISettingsProps): FactLadder | null {
    return ladderFor(operationsOf(settings), ceilingOf(settings));
}

/**
 * Whether this sheet is a level test. Every consumer asks this, so none can
 * disagree about it: a math-facts sheet, with a level chosen, whose operations
 * have a ladder. A composed format (color by number, spiral review) clears the
 * level before it asks the generator back, so it never reaches here set.
 */
export function levelApplies(settings: ISettingsProps): boolean {
    return (
        settings?.Format === ProblemFormatOptions.MathFacts &&
        (settings.FactLevel ?? 0) > 0 &&
        ladderForSettings(settings) !== null
    );
}

/** The sheet's level, clamped to its ladder; null when levels do not apply. */
export function levelOf(settings: ISettingsProps): FactLevel | null {
    if (!levelApplies(settings)) {
        return null;
    }
    const levels = ladderForSettings(settings)!.levels;
    const index = Math.min(settings.FactLevel, levels.length) - 1;
    return levels[index];
}

/** Every fact of the levels below this one: what a test reviews. */
export function reviewFacts(ladder: FactLadder, levelId: number): string[] {
    const facts = new Set<string>();
    for (const level of ladder.levels) {
        if (level.id >= levelId) break;
        level.focus.forEach((fact) => facts.add(fact));
    }
    return [...facts];
}

/**
 * Brings the level into line with the sheet's operations, in place. Called
 * after the operations change, beside `fitOneDigitRanges`: a level past the end
 * of the new ladder (x at level 10, then +) goes back to level 1.
 *
 * With no ladder, the level is left alone - that is the moment between two
 * clicks, and the picker says why nothing changed.
 */
export function fitFactLevel<T extends ISettingsProps>(settings: T): T {
    const level = settings.FactLevel ?? 0;
    if (level <= 0) {
        return settings;
    }
    const ladder = ladderFor(operationsOf(settings), ceilingOf(settings));
    if (ladder && level > ladder.levels.length) {
        settings.FactLevel = 1;
    }
    return settings;
}

// ─── Names ───────────────────────────────────────────────────────────────

const SET_NAMES: Record<LevelLanguage, Record<AdditionSet, string>> = {
    en: {
        zeroOne: "0s and 1s",
        two: "2s",
        doubles: "Doubles",
        nearDoubles: "Near doubles",
        makeTen: "Make ten",
        eight: "8s",
        rest: "The last eight",
    },
    es: {
        zeroOne: "0 y 1",
        two: "El 2",
        doubles: "Dobles",
        nearDoubles: "Casi dobles",
        makeTen: "Formar diez",
        eight: "El 8",
        rest: "Los últimos ocho",
    },
    fr: {
        zeroOne: "0 et 1",
        two: "Le 2",
        doubles: "Doubles",
        nearDoubles: "Presque doubles",
        makeTen: "Faire dix",
        eight: "Le 8",
        rest: "Les huit derniers",
    },
};

const MIXED: Record<LevelLanguage, string> = { en: "Mixed", es: "Mezcla", fr: "Mélange" };
const ZERO_ONE_TABLES: Record<LevelLanguage, string> = { en: "0s and 1s", es: "0 y 1", fr: "0 et 1" };

/**
 * A level's short name, as the picker lists it and the header prints it: "×4",
 * "÷4", "×4 ÷4", "Doubles", "Mixed". Symbols where they need no translation;
 * the strategy names in each language. Both the interface locale and a
 * worksheet's printed language are one of these three, so one function serves
 * both - the picker passes the locale, the header the printed language.
 */
export function levelName(level: FactLevel, ladderId: FactLadderId, language: LevelLanguage = "en"): string {
    const lang = SET_NAMES[language] ? language : "en";
    const { name } = level;
    if (name.kind === "mixed") {
        return MIXED[lang];
    }
    if (name.kind === "set") {
        return SET_NAMES[lang][name.set];
    }
    // The 0 and 1 level is not a single table, and ÷0 is not a fact, so it is
    // named in words on every multiplicative ladder.
    if (name.factors.length > 1) {
        return ZERO_ONE_TABLES[lang];
    }
    const n = name.factors[0];
    if (ladderId.startsWith("muldiv")) return `×${n} ÷${n}`;
    if (ladderId.startsWith("div")) return `÷${n}`;
    return `×${n}`;
}

// ─── Ladder titles ───────────────────────────────────────────────────────

const LADDER_TITLES: Record<FactLadderId, Record<LevelLanguage, string>> = {
    add: { en: "Addition facts", es: "Operaciones de suma", fr: "Faits d'addition" },
    sub: { en: "Subtraction facts", es: "Operaciones de resta", fr: "Faits de soustraction" },
    addsub: {
        en: "Addition and subtraction facts",
        es: "Operaciones de suma y resta",
        fr: "Faits d'addition et de soustraction",
    },
    mul10: { en: "Multiplication facts to 10", es: "Operaciones de multiplicación hasta 10", fr: "Faits de multiplication jusqu'à 10" },
    mul12: { en: "Multiplication facts to 12", es: "Operaciones de multiplicación hasta 12", fr: "Faits de multiplication jusqu'à 12" },
    div10: { en: "Division facts to 10", es: "Operaciones de división hasta 10", fr: "Faits de division jusqu'à 10" },
    div12: { en: "Division facts to 12", es: "Operaciones de división hasta 12", fr: "Faits de division jusqu'à 12" },
    muldiv10: {
        en: "Multiplication and division facts to 10",
        es: "Operaciones de multiplicación y división hasta 10",
        fr: "Faits de multiplication et de division jusqu'à 10",
    },
    muldiv12: {
        en: "Multiplication and division facts to 12",
        es: "Operaciones de multiplicación y división hasta 12",
        fr: "Faits de multiplication et de division jusqu'à 12",
    },
};

/**
 * What a ladder is called in print, for the title of the tracking chart (ADR 0071 §8). Plain
 * ASCII apostrophes: this is printed, and the printed alphabet is WinAnsi (ADR 0070).
 */
export function ladderTitle(ladderId: FactLadderId, language: LevelLanguage = "en"): string {
    return LADDER_TITLES[ladderId][LADDER_TITLES[ladderId][language] ? language : "en"];
}
